/**
 * Theme-aware colours for the Recharts surfaces (Dashboard, Billing).
 *
 * Recharts takes colours as props rather than CSS classes, so these values
 * cannot come from the `dark:` utilities or the CSS ramp - they have to be
 * resolved in JS from the current theme. Before this existed the charts were
 * theme-blind: the grid was hardcoded to #f0f0f0, which is invisible on a dark
 * surface, and the series palette used 500/600-weight colours that vibrate
 * against dark backgrounds.
 *
 * Dark values are the lighter, less saturated steps of the same hues. Saturated
 * colours optically vibrate on dark grounds, and the lighter steps also clear
 * the >=4.5:1 needed for the legend and axis labels that sit beside them.
 *
 * Usage: `const chart = getChartTheme(isDark)` with `isDark` from useTheme().
 */

// Categorical series palette. Hue order is preserved between themes so a given
// series keeps its identity when the user switches light/dark.
const SERIES_LIGHT = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
  '#ec4899', // pink
  '#14b8a6', // teal
  '#a855f7', // purple
  '#eab308', // yellow
  '#6366f1', // indigo
  '#22c55e', // green
  '#f43f5e', // rose
  '#0ea5e9', // sky
]

const SERIES_DARK = [
  '#60a5fa', // blue-400
  '#34d399', // emerald-400
  '#fbbf24', // amber-400
  '#a78bfa', // violet-400
  '#f87171', // red-400
  '#22d3ee', // cyan-400
  '#fb923c', // orange-400
  '#a3e635', // lime-400
  '#f472b6', // pink-400
  '#2dd4bf', // teal-400
  '#c084fc', // purple-400
  '#facc15', // yellow-400
  '#818cf8', // indigo-400
  '#4ade80', // green-400
  '#fb7185', // rose-400
  '#38bdf8', // sky-400
]

// Status colours for the donut / status breakdowns.
const STATUS_LIGHT = {
  completed: '#22c55e',
  reviewing: '#f97316',
  processing: '#3b82f6',
  pending: '#eab308',
  cancelled: '#6b7280',
}

const STATUS_DARK = {
  completed: '#4ade80',
  reviewing: '#fb923c',
  processing: '#60a5fa',
  pending: '#facc15',
  cancelled: '#9ca3af',
}

export function getChartTheme(isDark) {
  return {
    series: isDark ? SERIES_DARK : SERIES_LIGHT,
    status: isDark ? STATUS_DARK : STATUS_LIGHT,

    /** The "Other" bucket and any neutral series. */
    neutral: isDark ? '#9ca3af' : '#9ca3af',

    /** Gridlines: must be visible without competing with the data. */
    grid: isDark ? '#2e2e33' : '#f0f0f0',

    /** Axis tick labels. */
    axis: isDark ? '#a8a8b2' : '#9ca3af',

    /** Unfilled donut / progress track. */
    track: isDark ? '#2e2e33' : '#e5e7eb',

    /** Floating tooltip surface — matches the raised card surface. */
    tooltipBg: isDark ? '#1c1c1f' : '#ffffff',
    tooltipBorder: isDark ? '#2e2e33' : '#e5e7eb',
    tooltipText: isDark ? '#ededf0' : '#111827',
  }
}

export { SERIES_LIGHT, SERIES_DARK }
